import { useState, useEffect, memo } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mui/system';
import Drawer from '@mui/material/Drawer';
import { Avatar, Grid, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Link from 'next/link';

import theme from 'src/style-system/theme';
import { AppRoutes } from '@utils/appRoutes';
import { usePersistentplanAndFreq } from 'store/planAndFreq';
import { removeCartCount, removePantryCartCount } from 'store/cartItems';
import CartBag from '@public/assets/svg/CartBag.svg';
import { headerList, baseHeaderList } from '@utils/data';
import TCCLogo from 'svgComponents/TccLogo';
import CrossSign from '@assets/images/svg/CrossSign.svg';
import WordMarkIcon from 'svgComponents/WordMarkIcon';
import BurgerMenuIcon from 'svgComponents/BurgerMenuIcon';
import useAuth from '@utils/useAuth';
import { ICountryList } from '@utils/types';
import CountryDrawer from '@components/CountryDrawer';
import { usePersistentSelectedCountry } from 'store/selectedCountry';
import {
  promotionalUpdatesVar,
  selectAddressIdVar,
  subscriptionIdStateVar,
} from 'store/utils';
import { updateShippingFormInput } from 'store/shippingFormInput';
import { initialValues } from '@utils/validateShippingForm';
import { promoCodeDetailsVar, updatePromoCode } from 'store/promoCode';
import {
  mealCountWithoutSides,
  totalMealCountWithoutSides,
} from 'store/productsFlatList';
import { useReactiveVar } from '@apollo/client';

interface HeaderProps {
  isBackground?: boolean;
  isFixed?: boolean;
  showCartIcon?: boolean;
  onClickCartIcon?: any;
  isPlansPage?: boolean;
  fill?: string;
  LogoIcon?: any;
  burgerMenuFill?: string;
  getServiceableCountries?: ICountryList[];
  countryCode?: string;
  onClickCountry?: (countryCode: string) => void;
  TCCLogoFill?: any;
  type?: string;
}

const Header = ({
  isBackground = true,
  isFixed = false,
  showCartIcon = false,
  onClickCartIcon,
  isPlansPage = false,
  fill,
  LogoIcon,
  burgerMenuFill,
  getServiceableCountries,
  countryCode,
  TCCLogoFill,
  onClickCountry,
  type,
}: HeaderProps) => {
  const [isCountryDrawerOpen, setCountryDrawerOpen] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState<ICountryList>();

  const router = useRouter();
  const [open, setOpen] = useState(false);
  const planAndFreq = usePersistentplanAndFreq();

  const mealCounts = useReactiveVar(mealCountWithoutSides);

  const isAdmin = localStorage.getItem('isAdmin'); // for admin panel

  const selectedCountry = usePersistentSelectedCountry(
    getServiceableCountries,
    countryCode
  );

  const { setAuthState, authState, detectedCountryCode } = useAuth();

  const isAccountActivated = isAdmin || authState?.user?.isAccountActivated;

  const handleClick = () => {
    router.push(`/${detectedCountryCode}${AppRoutes.ORDER_STEP_TWO}`);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (selectedCountry) {
      setDefaultCountry(selectedCountry);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleLogout = (id: number) => {
    if (id === 5) {
      router.push(AppRoutes.HOME_ROUTE);
      localStorage.removeItem('tcc-jwt-token');
      setAuthState({});

      // reset store
      updateShippingFormInput(initialValues);
      updatePromoCode('');
      selectAddressIdVar(null);
      promotionalUpdatesVar(false);
      removeCartCount();
      removePantryCartCount();
      subscriptionIdStateVar('');
      promoCodeDetailsVar(null);
      localStorage.removeItem('planAndFreq');
      localStorage.removeItem('zipCode');
      localStorage.removeItem('tcc_subs_id');
      localStorage.removeItem('email');
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          position: isFixed ? 'fixed' : 'block',
          top: '-2px',
          zIndex: 15,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            backgroundColor: isBackground ? 'whitePepper.main' : '',
            padding: isBackground ? '12px 30px' : '12px 30px 0',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            [theme.breakpoints.up('md')]: {
              padding: isBackground ? '25px 50px' : '30px 50px 0',
            },
          }}
        >
          {getServiceableCountries?.length ? (
            <Avatar
              src={defaultCountry?.country_flag}
              alt={defaultCountry?.iso_alpha_2_code || 'US'}
              onClick={() => setCountryDrawerOpen(true)}
              sx={{
                cursor: 'pointer',
                width: { xs: '30px', md: '40px' },
                height: { xs: '30px', md: '40px' },
              }}
            />
          ) : (
            <Link href={AppRoutes.HOME_ROUTE}>
              <a>{LogoIcon ? <LogoIcon /> : <TCCLogo fill={TCCLogoFill} />}</a>
            </Link>
          )}

          <Link href={AppRoutes.HOME_ROUTE}>
            <a>
              <WordMarkIcon fill={fill} />
            </a>
          </Link>

          {showCartIcon && !isPlansPage ? (
            <Box
              sx={{
                position: 'relative',
              }}
            >
              <Box
                onClick={() => onClickCartIcon(true)}
                sx={{
                  padding: '0 25px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  columnGap: '15px',
                  backgroundColor: theme.palette.cinnamon.main,
                  width: '160px',
                  height: '116px',
                  cursor: 'pointer',
                  position: 'absolute',
                  right: '-50px',
                  top: '-60px',
                  [theme.breakpoints.down('md')]: {
                    display: 'none',
                  },
                }}
              >
                <CartBag />
                <Typography variant="body2" color="whitePepper.main">
                  {mealCounts}/{planAndFreq.selectedPlan}
                  {/* {type === 'order'
                    ? totalMealCounts
                    : planAndFreq.selectedPlan} */}
                </Typography>
              </Box>
            </Box>
          ) : (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerOpen}
              sx={{ ...(open && { display: 'none' }) }}
            >
              <BurgerMenuIcon fill={burgerMenuFill || fill} />
            </IconButton>
          )}
        </Box>

        <Drawer
          sx={{
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: '100vw',
              height: '100vh',
              backgroundColor: 'garamMasala.main',
              padding: '32px 35px',
              display: 'flex',
              justifyContent: 'space-between',
            },
          }}
          variant="persistent"
          anchor="top"
          open={open}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Link href={AppRoutes.HOME_ROUTE}>
              <a>{LogoIcon ? <LogoIcon /> : <TCCLogo />}</a>
            </Link>

            <IconButton onClick={handleDrawerClose}>
              <CrossSign />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Grid container sx={{ rowGap: { xs: '25px', md: '65px' } }}>
              {headerList
                .filter(({ id }) => {
                  if (id === 7) {
                    if (isAccountActivated) {
                      return false;
                    }
                    return true;
                  }
                  if (id === 6) {
                    if (!isAccountActivated) {
                      return false;
                    }
                    return true;
                  }
                  return true;
                })
                .map((item) => (
                  <Grid item xs={12} md={6} key={item.id}>
                    {item.link === AppRoutes.ORDER_STEP_ONE ? (
                      <Typography
                        variant="h1"
                        align="center"
                        color="turmeric.main"
                        sx={{ cursor: 'pointer' }}
                        onClick={handleClick}
                      >
                        {item.value}
                      </Typography>
                    ) : (
                      <Link
                        href={
                          item.link === AppRoutes.ORDER_STEP_ONE
                            ? `/${detectedCountryCode}${AppRoutes.ORDER_STEP_ONE}`
                            : item.link
                        }
                      >
                        <Typography
                          variant="h1"
                          align="center"
                          color="turmeric.main"
                          sx={{ cursor: 'pointer' }}
                        >
                          {item.value}
                        </Typography>
                      </Link>
                    )}
                  </Grid>
                ))}
            </Grid>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              [theme.breakpoints.up('md')]: {
                justifyContent: 'center',
                gap: '100px',
              },
            }}
          >
            {/*Before changes baseHeaderList make sure to check the logic logout  */}
            {baseHeaderList
              .filter(({ id }) =>
                !isAccountActivated && id === 5 ? false : true
              )
              .map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    [theme.breakpoints.up('md')]: {
                      cursor: 'pointer',
                    },
                  }}
                >
                  {item.link ? (
                    <Link href={item.link}>
                      <Typography
                        variant="body1"
                        align="center"
                        color="cinnamon.main"
                      >
                        {item.value}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography
                      variant="body1"
                      align="center"
                      color="cinnamon.main"
                      onClick={() => handleLogout(item.id)}
                    >
                      {item.value}
                    </Typography>
                  )}
                </Box>
              ))}
          </Box>
        </Drawer>
      </Box>
      <CountryDrawer
        getServiceableCountries={getServiceableCountries}
        isCountryDrawerOpen={isCountryDrawerOpen}
        setCountryDrawerOpen={setCountryDrawerOpen}
        onClickCountry={onClickCountry}
      />
    </>
  );
};

export default memo(Header);
